import { plaidCategoryPhrase } from '../shared/plaidCategory'
import type {
  SeparationQuestion,
  SeparationSubItem,
} from '@/features/onboarding/separation'

/** Capped, because a 48-row group would otherwise put 48 account names in one sentence. */
const distinctSubItemField = (
  question: SeparationQuestion,
  read: (subItem: SeparationSubItem) => string | null | undefined,
  limit: number
): string[] =>
  [
    ...new Set(
      question.subItems
        .map(read)
        .filter((value): value is string => Boolean(value?.trim()))
    ),
  ].slice(0, limit)

const OPENING =
  "I've connected my bank accounts to Heard and I'm sorting my transactions into business and personal."

const PLAIN_LANGUAGE = 'Keep it short and skip the tax jargon.'

const joinLines = (lines: (string | null)[]): string =>
  lines.filter((line): line is string => line !== null).join(' ')

const listWithOr = (items: string[]) =>
  items.length > 1
    ? `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`
    : (items[0] ?? '')

const choice = (label: string, caption: string | undefined) =>
  caption
    ? `"${label}" (${caption.charAt(0).toLowerCase()}${caption.slice(1)})`
    : `"${label}"`

/**
 * Closes with the group's own question and choices, because a transfer has no
 * business/personal answer and Haven picks whichever vocabulary it is asked in.
 */
const askWhichChoice = (
  question: SeparationQuestion,
  choices: { label: string; caption?: string }[]
) => [
  `Heard is asking me: ${question.questionText}`,
  choices.length > 0
    ? `My choices are ${listWithOr(choices.map(({ label, caption }) => choice(label, caption)))}. As a therapist, which should I pick?`
    : 'As a therapist, what should I answer?',
]

const groupChoices = (question: SeparationQuestion) =>
  question.toggleOptions.filter(({ value }) => value !== question.expandValue)

/**
 * Asks about the whole merchant. Carries the bank descriptions, Plaid's category and the
 * rows, because a name and a total alone leave Haven nothing to answer from.
 */
export const buildHavenGroupPrompt = (question: SeparationQuestion): string => {
  const accounts = distinctSubItemField(
    question,
    (subItem) => subItem.accountName,
    3
  )
  const [description, ...otherDescriptions] = distinctSubItemField(
    question,
    (subItem) => subItem.description,
    2
  )
  const categories = distinctSubItemField(
    question,
    (subItem) => subItem.plaidCategory,
    2
  ).map(plaidCategoryPhrase)

  const shown = question.subItems.slice(0, 5)
  const rows = shown
    .map((subItem) => [subItem.label, subItem.meta].filter(Boolean).join(': '))
    .filter(Boolean)
  const unshown = question.subItems.length - shown.length

  const isSuggested =
    question.suggested === 'business' || question.suggested === 'personal'

  return joinLines([
    OPENING,
    `I'm on ${question.headerName}: ${question.headerSubtext}, ${question.amount} in total.`,
    description
      ? `My bank shows them as "${description}"${otherDescriptions.length > 0 ? ' and similar' : ''}.`
      : null,
    categories.length > 0
      ? `They came through categorized as ${categories.join(' and ')}.`
      : null,
    rows.length > 0
      ? `The charges are ${rows.join('; ')}${unshown > 0 ? `, plus ${unshown} more` : ''}.`
      : null,
    accounts.length > 0 ? `They're on ${accounts.join(' and ')}.` : null,
    isSuggested ? `Heard suggests ${question.suggested}.` : null,
    ...askWhichChoice(question, groupChoices(question)),
    PLAIN_LANGUAGE,
  ])
}
